import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useSuspenseQuery } from '@tanstack/react-query';
import * as AlertNotification from '@givebutter/primitives/alert-notification';
import { Badge } from '@givebutter/primitives/badge';
import { Box } from '@givebutter/primitives/box';
import { BoxIcon } from '@givebutter/primitives/box-icon';
import { Button } from '@givebutter/primitives/button';
import { faCog, faSync } from '@fortawesome/pro-regular-svg-icons';
import { faExclamationTriangle, faWarning } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as ManualSyncDialog from './ManualSyncDialog';
import { fetchQuickBooksRules, fetchQuickBooksSetup } from 'api/quickBooks';
import * as DisconnectQuickBooksDialog from 'assets/js/pages/account-settings/account-integrations/QuickBooks/DisconnectQuickBooksDialog';
import * as QuickBooks from 'assets/js/pages/account-settings/account-integrations/QuickBooks/QuickBooks';
import { openAuthWindow } from 'assets/js/pages/account-settings/account-integrations/QuickBooks/QuickBooksConnect';
import { QuickBooksTransactionsTable } from 'assets/js/pages/account-settings/account-integrations/QuickBooks/QuickBooksTransactionsTable';
import { useQuickBooksStatus } from 'assets/js/pages/account-settings/account-integrations/QuickBooks/useQuickBooksStatus';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';

const QuickBooksDashboard = () => {
    const { accountId } = useCurrentAccount();
    const { data } = useQuickBooksStatus();
    const { status } = data;
    const isActive = status === 'active';
    const isConnected = status === 'connected';
    const isDisconnected = status === 'disconnected';

    const { data: rules } = useSuspenseQuery({
        queryKey: accountQueryKeys.quickBooksRules(accountId),
        queryFn: () => fetchQuickBooksRules(accountId),
    });

    const { data: setup } = useSuspenseQuery({
        queryKey: accountQueryKeys.quickBooksSetup(accountId),
        queryFn: () => fetchQuickBooksSetup(accountId),
    });

    const { auth_url: authURL } = setup;

    const invalidRule = useMemo(() => rules.find(rule => rule.invalid), [rules]);

    return (
        <QuickBooks.Root>
            <QuickBooks.Header>
                <div className="flex items-center gap-2">
                    <QuickBooks.Logo />
                    <ConnectionStatusBadge />
                </div>
                <div className="flex items-center gap-2">
                    <Link to={`/accounts/${accountId}/settings/integrations/quickbooks/configure`}>
                        <Button
                            size="sm"
                            variant={isActive ? 'outline' : 'solid'}
                            color={isActive ? 'neutral' : 'primary'}
                        >
                            <FontAwesomeIcon icon={faCog} />
                            Settings
                        </Button>
                    </Link>
                    <ManualSyncDialog.Root>
                        <ManualSyncDialog.Trigger asChild>
                            <Button
                                size="sm"
                                disabled={!isActive}
                                variant={isActive ? 'solid' : 'outline'}
                                color={isActive ? 'primary' : 'neutral'}
                            >
                                <FontAwesomeIcon icon={faSync} />
                                Sync now
                            </Button>
                        </ManualSyncDialog.Trigger>
                    </ManualSyncDialog.Root>
                </div>
            </QuickBooks.Header>

            {isDisconnected && (
                <AlertNotification.Root type="info" className="rounded-xl">
                    <BoxIcon size="md">
                        <FontAwesomeIcon
                            size="lg"
                            className="text-warning-600"
                            icon={faExclamationTriangle}
                        />
                    </BoxIcon>
                    <div className="flex flex-col gap-3 text-sm">
                        <div>
                            <p className="font-medium">We lost connection!</p>
                            <p>
                                Please sign into your Intuit account to re-authorize your connection
                                and continue using the QuickBooks integration.
                            </p>
                        </div>
                        <div className="flex gap-4">
                            <Button
                                variant="none"
                                size="sm"
                                color="primary"
                                onClick={() => {
                                    authURL && openAuthWindow(authURL);
                                }}
                            >
                                Re-authorize connection
                            </Button>
                            <DisconnectQuickBooksDialog.Root>
                                <DisconnectQuickBooksDialog.Trigger asChild>
                                    <Button variant="none" size="sm">
                                        Disconnect
                                    </Button>
                                </DisconnectQuickBooksDialog.Trigger>
                            </DisconnectQuickBooksDialog.Root>
                        </div>
                    </div>
                </AlertNotification.Root>
            )}

            {!!invalidRule && (
                <AlertNotification.Root type="info" className="rounded-xl">
                    <BoxIcon size="md">
                        <FontAwesomeIcon
                            size="lg"
                            className="text-warning-600"
                            icon={faExclamationTriangle}
                        />
                    </BoxIcon>
                    <div className="flex flex-col gap-3 text-sm">
                        <div>
                            <p className="font-medium">
                                There are issue(s) with your QuickBooks mapping
                            </p>
                            <p>
                                Some of your mapping rule selections no longer exist in Givebutter
                                or QuickBooks. Transactions that match these rules may have issues
                                and fail to sync until the mappings are updated.
                            </p>
                        </div>
                        <Link
                            to={`/accounts/${accountId}/settings/integrations/quickbooks/configure/rules/${invalidRule.id}/edit`}
                        >
                            Update mapping
                        </Link>
                    </div>
                </AlertNotification.Root>
            )}

            {(isActive || isDisconnected) && <QuickBooksTransactionsTable />}

            {isConnected && (
                <Box className="flex min-h-52 flex-col items-center justify-center gap-4 p-6">
                    <BoxIcon size="lg" variant={'primary'}>
                        <FontAwesomeIcon icon={faWarning} className="text-gray-400" />
                    </BoxIcon>
                    <div className="flex flex-col items-center justify-center gap-1">
                        <p className="font-semibold">Configure your QuickBooks</p>
                        <p className="text-gray-600">
                            We won't start syncing transactions to QuickBooks until your
                            configuration is completed.
                        </p>
                    </div>
                </Box>
            )}
        </QuickBooks.Root>
    );
};

const ConnectionStatusBadge = () => {
    const { data } = useQuickBooksStatus();
    const { status } = data;

    const label = {
        active: 'Connected',
        disconnected: 'Disconnected',
        connected: 'Not configured',
        notConfigured: 'Not configured',
    }[status ?? 'notConfigured'];

    const color = (
        {
            active: 'success',
            disconnected: 'warning',
            connected: 'error',
            notConfigured: 'error',
        } as const
    )[status ?? 'notConfigured'];

    return (
        <Badge color={color} size="sm">
            {label}
        </Badge>
    );
};

export { QuickBooksDashboard };
